import { TrackedChange } from 'models/changes'
import {
    MutableRefObject,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useHandleActiveTab, useHandleLatestContentItem } from '..'
import { ScrollToContent } from '../store/scrollStore'
import { SCROLL_CONFIG } from '../utils/TreeNodesUtils'

const CONTENT_FETCH_TIMEOUT = 8000
// Constants for state coordination to prevent race conditions
export const CONTENT_FETCH_STATES = {
    READY_TO_FETCH: true,
    FETCH_COMPLETE: false,
} as const

interface SetPendingScrollRequest {
    scrollToId: string
    correspondingTrackChange: TrackedChange
    timestamp: number
}

export interface ScrollRequestExecutorParams {
    scrollToContent: ScrollToContent
    scrollToElementRef: MutableRefObject<(id: string) => Promise<void>>
    clearScrollToContentRef: MutableRefObject<() => void>
    setPendingScrollRequest: (req: SetPendingScrollRequest) => void
    isHandlingChangeTab: boolean
    safeDelay: (ms: number) => Promise<boolean>
    isCurrentRequest: (requestId: string) => boolean
}

/**
 * Hook responsible for executing scroll request operations
 * This ensures proper serialization of API calls without complex promise chaining.
 *
 * @param {ScrollRequestExecutorParams} params - Parameters for the scroll request executor
 * @returns {Object} - Contains the executeScrollRequest function
 */
export const useScrollRequestExecutor = ({
    scrollToElementRef,
    clearScrollToContentRef,
    setPendingScrollRequest,
    isHandlingChangeTab,
    safeDelay,
    isCurrentRequest,
}: ScrollRequestExecutorParams) => {
    const [isReadyForContentFetch, setIsReadyForContentFetch] = useState<
        boolean
    >(CONTENT_FETCH_STATES.FETCH_COMPLETE)
    const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const handleLatestContentItem = useHandleLatestContentItem(
        setIsReadyForContentFetch
    )
    const handleTrackChangeFromActiveTab = useHandleActiveTab(
        setIsReadyForContentFetch
    )
    const handleLatestContentItemRef = useRef(handleLatestContentItem)
    const handleTrackChangeFromActiveTabRef = useRef(
        handleTrackChangeFromActiveTab
    )

    handleTrackChangeFromActiveTabRef.current = handleTrackChangeFromActiveTab
    handleLatestContentItemRef.current = handleLatestContentItem

    const executeScrollRequest = useCallback(
        async (
            requestId: string,
            scrollToId: string,
            correspondingTrackChange: TrackedChange
        ): Promise<void> => {
            try {
                // Step 1: Handle tab switching
                const tabSwitchResult = await handleTrackChangeFromActiveTabRef.current(
                    correspondingTrackChange.partType
                )
                const isArticleLevel =
                    correspondingTrackChange.contentItemId ===
                    correspondingTrackChange.rootContentItemId
                if (!isCurrentRequest(requestId)) return

                if (!tabSwitchResult) {
                    clearScrollToContentRef.current()
                    return
                }

                // Step 2: Wait for tab switch if needed
                if (isHandlingChangeTab && !isArticleLevel) {
                    const isStillMounted = await safeDelay(
                        SCROLL_CONFIG.DELAYS.TAB_SWITCH
                    )

                    if (!isCurrentRequest(requestId) || !isStillMounted) return
                }

                if (!isCurrentRequest(requestId)) return

                // Wait for tab switch to signal that content loading can begin
                if (isReadyForContentFetch) {
                    // Step 3: Handle content item
                    const hasContentItemElement = await handleLatestContentItemRef.current(
                        correspondingTrackChange
                    )

                    if (!isCurrentRequest(requestId)) return

                    // Step 4: Scroll or set pending request
                    if (hasContentItemElement) {
                        await scrollToElementRef.current(scrollToId)
                        clearScrollToContentRef.current()
                    } else {
                        setPendingScrollRequest({
                            scrollToId,
                            correspondingTrackChange,
                            timestamp: Date.now(),
                        })
                        clearScrollToContentRef.current()
                    }
                }
            } catch (error) {
                console.error('Error handling scroll request:', error)
                setIsReadyForContentFetch(CONTENT_FETCH_STATES.FETCH_COMPLETE)
                if (isCurrentRequest(requestId)) {
                    clearScrollToContentRef.current()
                }
            }
        },
        [
            setPendingScrollRequest,
            isHandlingChangeTab,
            safeDelay,
            isCurrentRequest,
            isReadyForContentFetch,
        ]
    )

    // Fallback: If content fetch is not ready after timeout, reset the flag
    useEffect(() => {
        if (!isReadyForContentFetch) {
            fetchTimeoutRef.current = setTimeout(() => {
                setIsReadyForContentFetch(CONTENT_FETCH_STATES.READY_TO_FETCH)
            }, CONTENT_FETCH_TIMEOUT)
        } else if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current)
            fetchTimeoutRef.current = null
        }
        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current)
            }
        }
    }, [isReadyForContentFetch])

    return { executeScrollRequest }
}
